/**
 * Cost Tracking Dashboard Service
 * 
 * Remonte tous les coûts en temps réel:
 * - Par source API
 * - Par zoom level
 * - Par utilisateur
 * - Prédiction journalière
 * - Budget remaining
 */

import type { POIMinimal } from '../strategies/poi-multisource-strategy.js'

export interface CostEvent {
  id: string
  timestamp: Date
  source: 'overpass' | 'mapbox' | 'google' | 'wikipedia' | 'custom'
  cost: number
  queries: number
  poiCount: number
  zoomLevel: number
  userId?: string
  detail: {
    description: string
    costPerQuery: number
    cached: boolean
  }
}

export interface DailyBudget {
  date: string
  spent: number
  limit: number
  remaining: number
  percentage: number // 0-100
  projectedDaily: number // If pattern continues
  status: 'ok' | 'warning' | 'critical' // >80% = warning, >95% = critical
}

export interface SourceCosts {
  source: string
  totalCost: number
  queryCount: number
  averageCostPerQuery: number
  costPercentage: number // Of total
  lastQuery?: Date
}

export interface ZoomLevelCosts {
  zoomLevel: number
  totalCost: number
  queryCount: number
  poiCount: number
  averageCostPerQuery: number
}

export interface HourlyMetrics {
  hour: number
  cost: number
  queries: number
  poiCount: number
  cacheHitRate: number
}

export interface DashboardData {
  currentBudget: DailyBudget
  sourceBreakdown: Record<string, SourceCosts>
  zoomBreakdown: ZoomLevelCosts[]
  hourlyMetrics: HourlyMetrics[]
  topUsers: Array<{
    userId: string
    queries: number
    cost: number
    percentage: number
  }>
  alerts: Array<{
    level: 'info' | 'warning' | 'critical'
    message: string
    timestamp: Date
  }>
  predictions: {
    projectedDaily: number
    trend: 'stable' | 'increasing' | 'decreasing'
    recommendedAction?: string
  }
}

// ============================================================================
// COST TRACKING SERVICE
// ============================================================================

export class CostTrackingService {
  private events: CostEvent[] = []
  private dailyBudget = 10 // Default $10/day
  private dailyStart: Date = new Date()

  constructor(dailyBudgetLimit: number = 10) {
    this.dailyBudget = dailyBudgetLimit
    this.resetDailyIfNeeded()
  }

  /**
   * Record a cost event
   */
  recordCost(
    source: string,
    cost: number,
    queryCount: number,
    poiCount: number,
    zoomLevel: number,
    userId?: string,
    cached: boolean = false
  ): void {
    const event: CostEvent = {
      id: `cost-${Date.now()}-${Math.random()}`,
      timestamp: new Date(),
      source: source as any,
      cost,
      queries: queryCount,
      poiCount,
      zoomLevel,
      userId,
      detail: {
        description: `${queryCount} queries from ${source} at zoom ${zoomLevel}`,
        costPerQuery: cost / queryCount,
        cached
      }
    }

    this.events.push(event)

    // Keep only last 24 hours
    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000)
    this.events = this.events.filter(e => e.timestamp > oneDayAgo)
  }

  /**
   * Get current budget status
   */
  getCurrentBudget(): DailyBudget {
    this.resetDailyIfNeeded()

    const todayEvents = this.getTodayEvents()
    const spent = todayEvents.reduce((sum, e) => sum + e.cost, 0)
    const remaining = this.dailyBudget - spent
    const percentage = (spent / this.dailyBudget) * 100

    // Project daily cost based on hourly rate
    const now = new Date()
    const hoursElapsed = (now.getTime() - this.dailyStart.getTime()) / (60 * 60 * 1000)
    const projectedDaily = hoursElapsed > 0 ? (spent / hoursElapsed) * 24 : 0

    // Determine status
    let status: 'ok' | 'warning' | 'critical' = 'ok'
    if (percentage > 95) status = 'critical'
    else if (percentage > 80) status = 'warning'

    return {
      date: this.dailyStart.toISOString().split('T')[0],
      spent: Number(spent.toFixed(4)),
      limit: this.dailyBudget,
      remaining: Number(remaining.toFixed(4)),
      percentage: Number(percentage.toFixed(1)),
      projectedDaily: Number(projectedDaily.toFixed(2)),
      status
    }
  }

  /**
   * Get cost breakdown by source
   */
  getSourceBreakdown(): Record<string, SourceCosts> {
    const todayEvents = this.getTodayEvents()
    const breakdown: Record<string, SourceCosts> = {}
    const totalCost = todayEvents.reduce((sum, e) => sum + e.cost, 0)

    for (const event of todayEvents) {
      if (!breakdown[event.source]) {
        breakdown[event.source] = {
          source: event.source,
          totalCost: 0,
          queryCount: 0,
          averageCostPerQuery: 0,
          costPercentage: 0,
          lastQuery: event.timestamp
        }
      }

      breakdown[event.source].totalCost += event.cost
      breakdown[event.source].queryCount += event.queries
      breakdown[event.source].lastQuery = event.timestamp
    }

    // Calculate averages and percentages
    for (const source in breakdown) {
      const item = breakdown[source]
      item.averageCostPerQuery = item.queryCount > 0 ? item.totalCost / item.queryCount : 0
      item.costPercentage = totalCost > 0 ? (item.totalCost / totalCost) * 100 : 0

      item.totalCost = Number(item.totalCost.toFixed(4))
      item.averageCostPerQuery = Number(item.averageCostPerQuery.toFixed(6))
      item.costPercentage = Number(item.costPercentage.toFixed(1))
    }

    return breakdown
  }

  /**
   * Get cost breakdown by zoom level
   */
  getZoomBreakdown(): ZoomLevelCosts[] {
    const todayEvents = this.getTodayEvents()
    const breakdown: Record<number, ZoomLevelCosts> = {}

    for (const event of todayEvents) {
      if (!breakdown[event.zoomLevel]) {
        breakdown[event.zoomLevel] = {
          zoomLevel: event.zoomLevel,
          totalCost: 0,
          queryCount: 0,
          poiCount: 0,
          averageCostPerQuery: 0
        }
      }

      breakdown[event.zoomLevel].totalCost += event.cost
      breakdown[event.zoomLevel].queryCount += event.queries
      breakdown[event.zoomLevel].poiCount += event.poiCount
    }

    // Calculate averages
    const result = Object.values(breakdown).map(item => ({
      ...item,
      averageCostPerQuery:
        item.queryCount > 0 ? Number((item.totalCost / item.queryCount).toFixed(6)) : 0,
      totalCost: Number(item.totalCost.toFixed(4))
    }))

    return result.sort((a, b) => a.zoomLevel - b.zoomLevel)
  }

  /**
   * Get hourly metrics
   */
  getHourlyMetrics(): HourlyMetrics[] {
    const todayEvents = this.getTodayEvents()
    const metrics: Record<number, HourlyMetrics> = {}

    for (const event of todayEvents) {
      const hour = event.timestamp.getHours()

      if (!metrics[hour]) {
        metrics[hour] = {
          hour,
          cost: 0,
          queries: 0,
          poiCount: 0,
          cacheHitRate: 0
        }
      }

      metrics[hour].cost += event.cost
      metrics[hour].queries += event.queries
      metrics[hour].poiCount += event.poiCount
    }

    // Calculate cache hit rate (cached events are free)
    const cachedEvents = todayEvents.filter(e => e.detail.cached)
    for (const hour in metrics) {
      const hourEvents = todayEvents.filter(e => e.timestamp.getHours() === parseInt(hour))
      const cachedCount = hourEvents.filter(e => e.detail.cached).length
      metrics[hour].cacheHitRate = hourEvents.length > 0 ? (cachedCount / hourEvents.length) * 100 : 0
      metrics[hour].cacheHitRate = Number(metrics[hour].cacheHitRate.toFixed(1))
      metrics[hour].cost = Number(metrics[hour].cost.toFixed(4))
    }

    return Object.values(metrics).sort((a, b) => a.hour - b.hour)
  }

  /**
   * Get top users by cost
   */
  getTopUsers(limit: number = 10): Array<{
    userId: string
    queries: number
    cost: number
    percentage: number
  }> {
    const todayEvents = this.getTodayEvents().filter(e => e.userId)
    const userMap: Record<string, { queries: number; cost: number }> = {}

    for (const event of todayEvents) {
      if (!userMap[event.userId!]) {
        userMap[event.userId!] = { queries: 0, cost: 0 }
      }
      userMap[event.userId!].queries += event.queries
      userMap[event.userId!].cost += event.cost
    }

    const totalCost = todayEvents.reduce((sum, e) => sum + e.cost, 0)

    return Object.entries(userMap)
      .map(([userId, data]) => ({
        userId,
        ...data,
        cost: Number(data.cost.toFixed(4)),
        percentage: Number(((data.cost / totalCost) * 100).toFixed(1))
      }))
      .sort((a, b) => b.cost - a.cost)
      .slice(0, limit)
  }

  /**
   * Get alerts
   */
  getAlerts(): Array<{
    level: 'info' | 'warning' | 'critical'
    message: string
    timestamp: Date
  }> {
    const alerts: Array<{
      level: 'info' | 'warning' | 'critical'
      message: string
      timestamp: Date
    }> = []

    const budget = this.getCurrentBudget()

    // Budget alerts
    if (budget.status === 'critical') {
      alerts.push({
        level: 'critical',
        message: `⚠️ Budget critical: ${budget.percentage.toFixed(1)}% spent ($${budget.spent}/$${budget.limit})`,
        timestamp: new Date()
      })
    } else if (budget.status === 'warning') {
      alerts.push({
        level: 'warning',
        message: `⚠️ Budget warning: ${budget.percentage.toFixed(1)}% spent ($${budget.spent}/$${budget.limit})`,
        timestamp: new Date()
      })
    }

    // Cost projection alert
    if (budget.projectedDaily > budget.limit) {
      alerts.push({
        level: 'warning',
        message: `📈 Projected daily cost: $${budget.projectedDaily} (exceeds limit of $${budget.limit})`,
        timestamp: new Date()
      })
    }

    // Source-specific alerts
    const sourceBreakdown = this.getSourceBreakdown()
    if (sourceBreakdown.google && sourceBreakdown.google.costPercentage > 50) {
      alerts.push({
        level: 'info',
        message: `💰 Google APIs are ${sourceBreakdown.google.costPercentage.toFixed(1)}% of costs. Consider using cheaper sources.`,
        timestamp: new Date()
      })
    }

    return alerts
  }

  /**
   * Get complete dashboard data
   */
  getDashboard(): DashboardData {
    const budget = this.getCurrentBudget()
    const sourceBreakdown = this.getSourceBreakdown()
    const zoomBreakdown = this.getZoomBreakdown()
    const hourlyMetrics = this.getHourlyMetrics()
    const topUsers = this.getTopUsers(5)
    const alerts = this.getAlerts()

    // Trend analysis
    const last2hours = hourlyMetrics.slice(-2)
    let trend: 'stable' | 'increasing' | 'decreasing' = 'stable'
    let recommendedAction: string | undefined

    if (last2hours.length === 2) {
      const prev = last2hours[0].cost
      const curr = last2hours[1].cost
      if (curr > prev * 1.2) {
        trend = 'increasing'
        recommendedAction = 'Consider limiting queries to cheaper sources'
      } else if (curr < prev * 0.8) {
        trend = 'decreasing'
        recommendedAction = 'Good! Cache is working well'
      }
    }

    return {
      currentBudget: budget,
      sourceBreakdown,
      zoomBreakdown,
      hourlyMetrics,
      topUsers,
      alerts,
      predictions: {
        projectedDaily: budget.projectedDaily,
        trend,
        recommendedAction
      }
    }
  }

  /**
   * Reset daily if new day started
   */
  private resetDailyIfNeeded(): void {
    const now = new Date()
    if (now.getDate() !== this.dailyStart.getDate()) {
      this.dailyStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    }
  }

  /**
   * Get events from today
   */
  private getTodayEvents(): CostEvent[] {
    const startOfDay = new Date(
      this.dailyStart.getFullYear(),
      this.dailyStart.getMonth(),
      this.dailyStart.getDate()
    )
    const endOfDay = new Date(startOfDay.getTime() + 24 * 60 * 60 * 1000)

    return this.events.filter(e => e.timestamp >= startOfDay && e.timestamp < endOfDay)
  }

  /**
   * Get raw events (for debugging)
   */
  getEvents(limit: number = 100): CostEvent[] {
    return this.events.slice(-limit)
  }

  /**
   * Update daily budget limit
   */
  setDailyBudget(amount: number): void {
    this.dailyBudget = amount
  }
}

export default CostTrackingService
